import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'or-theme';

/**
 * Owns the app-wide light/dark theme by stamping `data-theme` on the document
 * root. Tokens live in `src/styles/design-system.css` (var(--or-*)).
 *
 * Surfaces must step luminance (page → card → input → dialog). Chrome must
 * reference semantic tokens — see `dark-mode-bridge.css` for legacy remaps.
 *
 * SSR-safe: every DOM/storage access is guarded by isPlatformBrowser.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** Apply the persisted theme once on app boot. */
  init(): void {
    if (!this.isBrowser) return;
    this.apply(this.current());
  }

  /** Current theme — persisted value, or 'light' by default. */
  current(): ThemeMode {
    if (!this.isBrowser) return 'light';
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'dark' || saved === 'light' ? saved : 'light';
  }

  /** Set and persist a theme. */
  set(mode: ThemeMode): void {
    if (!this.isBrowser) return;
    localStorage.setItem(STORAGE_KEY, mode);
    this.apply(mode);
  }

  /** Flip between light and dark; returns the new mode. */
  toggle(): ThemeMode {
    const next: ThemeMode = this.current() === 'dark' ? 'light' : 'dark';
    this.set(next);
    return next;
  }

  private apply(mode: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', mode);
  }
}
